import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    AppStateFacade,
    Theme,
} from '@app-state';
import { IUserProfile } from '@models';
import { hasSupportForPrefersColorScheme } from '@uipath/angular/utilities';
import UiValidators, {
    UI_EMAIL_MAX_LENGTH,
    UI_NAME_MAX_LENGTH,
} from '@validators';

import {
    Observable,
    Subject,
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'ui-user-profile-general',
    templateUrl: './user-profile-general.component.html',
    styleUrls: [
        '../../components/ui-forms/ui-setting-form.scss',
        './user-profile-general.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiUserProfileGeneralComponent implements OnInit, OnDestroy {
    @Input()
    public set user({ name, surname, emailAddress, userName, tenantDisplayName }: IUserProfile) {
        this._user = {
            name,
            surname,
            emailAddress,
            userName,
            tenantDisplayName,
        };
    }
    public get user(): IUserProfile {
        return this._user;
    }
    @Input()
    public isLoading!: boolean;
    @Input()
    public isActiveUser!: boolean;

    @Output()
    public submitUser = new EventEmitter<IUserProfile>();
    @Output()
    public enterViewMode = new EventEmitter();

    public hasSupportForPrefersColorScheme = hasSupportForPrefersColorScheme();
    public disallowEmailChange = true;
    public formGroup!: FormGroup;
    public edit?: boolean;
    public theme$: Observable<Theme>;

    public get themes() {
        return Object.keys(Theme).filter(theme => theme !== Theme.auto);
    }

    public autoTheme = Theme.auto;

    private _user = {} as IUserProfile;
    private _destroyed$ = new Subject();

    constructor(
        private _appState: AppStateFacade,
        private _fb: FormBuilder,
        private _cd: ChangeDetectorRef,
    ) {
        this.disallowEmailChange = !this._appState.authConfig.value.cloud.allowSelfEmailUpdate;
        this.theme$ = this._appState.user.theme.value$;
    }

    ngOnInit() {
        this.formGroup = this._fb.group({
            'name': [
                this.user.name,
                Validators.maxLength(UI_NAME_MAX_LENGTH),
            ],
            'surname': [
                this.user.surname,
                Validators.maxLength(UI_NAME_MAX_LENGTH),
            ],
            'emailAddress': [
                this.user.emailAddress || '',
                Validators.compose([
                    Validators.maxLength(UI_EMAIL_MAX_LENGTH),
                    UiValidators.email(true),
                ])],
        });

        this.formGroup
            .valueChanges
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(
                value => {
                    Object.assign(this._user, value);
                },
            );
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    public viewMode(emit = true) {
        this.edit = false;
        if (emit && this.formGroup.dirty) {
            this.enterViewMode.emit();
        }
        this._cd.markForCheck();
    }

    public editMode() {
        this.edit = true;
        this._cd.markForCheck();
    }

    public changeTheme(theme: Theme) {
        this._appState.user.theme.set(theme);
    }
}
